// pms-core / domain — the daily stay rollup: types + the pure derivations.
//
// `docs/METRICS-FEED-BRIEF.md` §5. This is a FUNCTIONAL CORE in the sense
// ARCHITECTURE.md §1 means it — types and pure functions, no invariant
// enforcement, no infrastructure. Everything here is arithmetic over a row a
// port handed us; nothing here knows the row came from Vega, or from a
// database at all.
//
// ── Why the rollup has its own domain file and not just a port type ────────
//
// Two things genuinely belong to the domain rather than to any adapter:
//
//   1. **Recouches.** `OCCUPANCY-CHAIN.md` §1 is categorical — "`Nr` is never
//      an input; it is derived, always, everywhere." Every source that ever
//      feeds this port must produce `Nr` the same way, so it is computed once,
//      here, from the bases. An adapter returning its own `Nr` would be a
//      second definition, and the whole reason ADR-0015 exists is that the
//      platform once had three definitions of load.
//
//   2. **The comparison against the board.** M2's gate is "prove the derived
//      figures match reality before trusting them" (§7). What counts as a
//      match, and which discrepancies are meaningful, is a domain judgement —
//      not a rendering detail, and not something to re-decide in each view.

import type { IsoDate } from './types.js';

/** One day of stay actuals, in BOTH units, exactly as a feed reports them.
 *
 *  These are the BASE quantities only. Anything derivable from them is absent
 *  on purpose — see `deriveStayRollup`. Meals are in heads (a breakfast is
 *  served to a person); occupancy, arrivals and departures are given in both
 *  because §5's argument is that they are different questions: 2026-08-30 is
 *  42 rooms / 64 people, 2026-09-09 is 33 rooms / 61 people. */
export interface StayDayBase {
  date: IsoDate;
  /** `No` — rooms occupied on the NIGHT of `date`. */
  roomsOccupied: number;
  /** `Na` — rooms arriving on `date`. */
  roomsArrivals: number;
  /** `Nd` — rooms departing on `date`. */
  roomsDepartures: number;
  /** Occupied rooms the source has not allocated a room number to yet.
   *  A subset of `roomsOccupied`, not an addition to it — an unallocated OTA
   *  booking consumes a room of its requested type (migration 0123). */
  roomsUnallocated: number;
  guestsAdults: number;
  guestsChildren: number;
  /** Heads arriving on `date`. */
  guestsArrivals: number;
  /** Heads departing on `date`. */
  guestsDepartures: number;
  /** Breakfasts SERVED on the MORNING of `date`, to the guests who slept the
   *  night before. */
  breakfasts: number;
  /** Demi-pensions dining on the EVENING of `date` — the guests who sleep the
   *  night of `date`. Confirmed by the operator 2026-09-01: "dinner: the night
   *  of the stay (they eat the night they arrive)". */
  halfBoards: number;

  // ── Both meal figures are SERVED counts, not sold ones (migration 0139) ────
  //
  // Until 0139 they were derived by filtering occupancy heads on the
  // réservation's meal plan, and that plan is one code for a whole stay: what
  // was SOLD. The source separately holds a per-réservation, per-day effectif —
  // what is SERVED — and the two differ in BOTH directions: a demi-pension
  // guest may skip dinner while keeping the `1/2 P` rate (no price break), and
  // a room-only guest may be fed and, per the operator, sometimes not billed.
  // Measured over a year: dinner differed on 57 reservation-nights (49 of them
  // ABOVE the plan), breakfast on 74.
  //
  // Nothing in the domain re-derives a meal from `board`. There is no `board`
  // here at all, and that is the point.

  // ── Parties (0126) — the third unit ───────────────────────────────────────
  //
  // A party is one réservation, so a group and a multi-room booking are each
  // ONE. Operator, 2026-09-01: "may be interesting to track number of parties
  // (a group = same parti; multi-room = one party)".
  //
  // It is a genuinely third dimension, not a rescaling of rooms or heads: a
  // 42-room group and 42 individual bookings are the same occupancy and a
  // completely different day — one arrival conversation against forty-two, one
  // breakfast seating together against a two-hour trickle.

  /** Réservations in house on the night of `date`. */
  partiesOccupied: number;
  partiesArrivals: number;
  partiesDepartures: number;
  /** Réservations at breakfast on the morning of `date`. */
  partiesBreakfast: number;
  /** Réservations dining demi-pension on the evening of `date`. */
  partiesHalfBoard: number;

  // ── The candidate half of each meal (0127, corrected by 0139) ─────────────
  //
  // Operator, 2026-09-01: the head count "is an indicator for both possible
  // 'dinner' candidates but also breakfast. people will decide if they will
  // have diner or breakfast". So every meal splits two ways:
  //
  //             served                     not served, could buy
  //   dinner    halfBoards                 guestsNoDinner    (derived)
  //   breakfast breakfasts                 guestsNoBreakfast (derived, 0139)
  //
  // `guestsNoBreakfast` used to be a BASE field, because under 0127 it meant
  // "heads not on bb/hb who slept last night" — a fact about the previous
  // night's meal plans that nothing else carried. Once `breakfasts` became a
  // served count that stopped being the right question, and the right one —
  // everyone who slept here and is not being served, however they are priced —
  // is `guestsLastNight - breakfasts`. So it moved to `StayDayRollup`, and the
  // port stopped returning it: §1's rule about `Nr` applies unchanged, and an
  // adapter carrying its own copy would be a second definition.

  /** Réservations who slept the night before and are served NO breakfast this
   *  morning. Stays a base: a count of réservations cannot be recovered from
   *  head counts. */
  partiesNoBreakfast: number;

  // ── Provenance of the two meal figures (0139) ─────────────────────────────
  //
  // The source writes no effectif row when there is nothing to serve, so a
  // missing row is the NORMAL shape for a room-only guest and is not evidence
  // of zero. A réservation without one falls back to its meal plan, and the
  // fallback is counted rather than hidden: a figure that is part measurement
  // and part forecast must be able to say so. Non-zero does not mean wrong — it
  // means "this many réservations were estimated".

  /** Réservations in house that night whose dinner figure came from the plan. */
  dinnerFromPlanParties: number;
  /** Réservations whose breakfast figure came from the plan. */
  breakfastFromPlanParties: number;

  /** The source's OWN head count for the night, from the effectif.
   *
   *  NOT occupancy, and deliberately not used as such: it can disagree with the
   *  rooming list (22 of 4088 reservation-nights measured, e.g. 34 against 36
   *  heads on 2026-09-07). Carried so the disagreement is visible to a view or
   *  an audit rather than buried — whether the platform should ever prefer it
   *  is an open decision, not a default. */
  guestsLodgingEffectif: number;

  /** Rooms the rooming list says are occupied whose réservation's effectif says
   *  NOBODY slept — the signature of a room the desk never released (0141).
   *
   *  A FLAG, not a correction. Occupancy deliberately does not follow it: the
   *  effectif lost to the invoice's billed room-nights in 1 of 13 measured
   *  cases, and a source wrong once in thirteen must not silently delete
   *  room-nights. The fix belongs in the source — release the room.
   *
   *  Counts only réservations that HAVE an effectif row: a present zero is
   *  evidence, a missing row is silence. */
  roomsNoLodging: number;

  /** Heads who slept the night of `date - 1`.
   *
   *  Carried so a view can render the meal rows against the population they
   *  belong to. Without it a table shows THIS MORNING's breakfast beside
   *  TONIGHT's occupancy and invites exactly the doubt it did on 2026-03-13,
   *  where a 42-room group departing produced 93 breakfasts on a day showing 8
   *  occupied rooms. Both numbers were right; nothing said they described
   *  different people.
   *
   *  Invariant: `breakfasts + guestsNoBreakfast === guestsLastNight`, which
   *  since 0139 holds BY CONSTRUCTION — `guestsNoBreakfast` is the remainder. */
  guestsLastNight: number;
}

/** A base day plus everything derivable from it. What a view renders. */
export interface StayDayRollup extends StayDayBase {
  /** `NoGuests` = adults + children. Infants are not counted: they occupy no
   *  bed and eat no cover, and §4.4 records that the source under-reports them
   *  anyway (20 rows in eight years). */
  guestsOccupied: number;
  /** `Nr` = `No - Na`, identity (A) of `OCCUPANCY-CHAIN.md` §2. Never stored,
   *  never read from a source. */
  roomsStayovers: number;
  /** `NrGuests` — the same identity in heads. §5: "the chain is unit-agnostic". */
  guestsStayovers: number;
  /** The same identity in parties. */
  partiesStayovers: number;

  /** In-house guests that evening whose rate does NOT include dinner —
   *  `guestsOccupied - halfBoards`. The dinner half of the candidate split;
   *  `guestsNoBreakfast` is the breakfast half, and comes from the source
   *  because it belongs to a different night.
   *
   *  This is what the head count is FOR. Operator, 2026-09-01: "total num of
   *  people is indicative of potential num of people eating 'a la carte' ie not
   *  Demi-Pension".
   *
   *  ⚠ An INDICATOR, not a count, and the difference is load-bearing. It is the
   *  pool of people who COULD eat à la carte tonight; most of them will not, and
   *  walk-ins from outside the hotel are not in it at all. The board's `diners`
   *  stays the desk's to type (§6.1) precisely because no reservation exists for
   *  it — this bounds the guess, it does not replace it. Presenting it as a
   *  cover count would be the feed inventing covers, which is the class of error
   *  §4.4 refuses for children. The same caveat applies to `guestsNoBreakfast`. */
  guestsNoDinner: number;

  /** Guests who slept last night and are NOT served breakfast this morning —
   *  `guestsLastNight - breakfasts`. Breakfast's candidate half, and the exact
   *  counterpart of `guestsNoDinner`.
   *
   *  ⚠ The same INDICATOR caveat: these people could buy a breakfast, and most
   *  will not. Since 0139 it also includes a guest whose rate DOES include one
   *  and who is not taking it — the honest reading of "could be served and is
   *  not", and the reason the pre-0139 rate-based definition had to go. */
  guestsNoBreakfast: number;
}

/** Provenance for a rollup, so a view can say WHEN this was last true.
 *
 *  §6.2 requires the figures to be displayed with their import timestamp
 *  rather than as editable-looking boxes. That makes provenance part of the
 *  port's contract, not a debugging extra: a number with no "as of" is exactly
 *  the silent-staleness this feed exists to remove. */
export interface StayFeedProvenance {
  /** When the feed last applied an import. Null before the first one. */
  importedAt: string | null;
  /** The source's own freshness — for Vega, the data file's mtime. Null when
   *  the source does not expose one. */
  sourceChangedAt: string | null;
  /** Window the last import actually covers. Outside it the rollup is empty
   *  because nothing was imported, NOT because the hotel was empty — a
   *  distinction a view must be able to make. */
  windowStart: IsoDate | null;
  windowEnd: IsoDate | null;
  /** False when the last attempt was refused (§6.5): the figures below are the
   *  previous good import, and saying so is the point. */
  lastAttemptApplied: boolean;
  /** Why the last attempt was refused, when it was. */
  refusalReason: string | null;
}

const heads = (d: StayDayBase) => d.guestsAdults + d.guestsChildren;

/** Add the derived quantities to one base day. Total, pure, order-free. */
export function deriveStayDay(base: StayDayBase): StayDayRollup {
  const guestsOccupied = heads(base);
  return {
    ...base,
    guestsOccupied,
    // Clamped at zero. The identity cannot go negative on consistent data —
    // arrivals are a subset of the occupied — but a source that reported
    // otherwise should produce a visibly wrong recouche count, not a negative
    // one that propagates into a staffing formula as a credit.
    roomsStayovers: Math.max(0, base.roomsOccupied - base.roomsArrivals),
    guestsStayovers: Math.max(0, guestsOccupied - base.guestsArrivals),
    // Parties do NOT satisfy the chain the way rooms and heads do: one
    // réservation can arrive and still be in house, so a party counted as
    // arriving is also counted as occupied and the subtraction is only a
    // stayover count when no room of it arrived today. It is right for the
    // common case and the clamp keeps it sane; it is not an identity, and it is
    // not published as one.
    partiesStayovers: Math.max(0, base.partiesOccupied - base.partiesArrivals),
    // Demi-pension is a subset of the guests sleeping that night, so this
    // cannot go negative on consistent data. Clamped anyway — see above.
    guestsNoDinner: Math.max(0, guestsOccupied - base.halfBoards),
    // Breakfast's counterpart, and the ONE definition of it (0139). Served
    // breakfasts are a subset of the guests who slept, so this cannot go
    // negative on consistent data — but a source that reported more breakfasts
    // than sleepers must produce a visible zero here, not a negative that
    // propagates into a staffing formula as a credit.
    guestsNoBreakfast: Math.max(0, base.guestsLastNight - base.breakfasts),
  };
}

export const deriveStayRollup = (days: readonly StayDayBase[]): StayDayRollup[] =>
  days.map(deriveStayDay);

// ── Comparing the feed against what the board holds (M2's gate) ─────────────
//
// M2 shows the rollup BESIDE the manual board rather than over it, so the
// operator can see whether the derived figures match reality before anything
// depends on them (§7). The comparison is domain logic because "do these
// agree" is a judgement about what the numbers mean, and it has to be the same
// judgement in the day view, the week view and any audit surface.

/** The board's own figures for a day, as far as this comparison cares. Null
 *  where the desk never typed anything — which is the common case, and the
 *  reason the feed exists. */
export interface BoardDayFigures {
  date: IsoDate;
  occupancy: number | null;
  arrivals: number | null;
  departures: number | null;
  breakfasts: number | null;
  halfBoards: number | null;
}

export type StayComparisonVerdict =
  /** The board holds nothing to compare — never typed, or typed as blank. */
  | 'boardSilent'
  /** Every compared figure agrees exactly. */
  | 'agree'
  /** At least one figure differs. */
  | 'differ';

export interface StayFieldDelta {
  field: 'occupancy' | 'arrivals' | 'departures' | 'breakfasts' | 'halfBoards';
  /** What the desk typed. */
  board: number | null;
  /** What the feed derives. */
  feed: number;
  /** `feed - board`, or null when the board is silent on this field. */
  delta: number | null;
}

export interface StayDayComparison {
  date: IsoDate;
  verdict: StayComparisonVerdict;
  feed: StayDayRollup;
  /** Only the fields that actually differ, in display order. Empty when the
   *  verdict is `agree` or `boardSilent`. */
  deltas: StayFieldDelta[];
}

/** Compare one day. A field the board never carried is NOT a discrepancy —
 *  silence is the normal state of an un-entered board day, and reporting it as
 *  a mismatch would drown the real ones. That distinction is the difference
 *  between a comparison an operator reads and one they learn to ignore. */
export function compareStayDay(
  feed: StayDayRollup,
  board: BoardDayFigures | null,
): StayDayComparison {
  const pairs: { field: StayFieldDelta['field']; board: number | null; feed: number }[] = [
    { field: 'occupancy', board: board?.occupancy ?? null, feed: feed.roomsOccupied },
    { field: 'arrivals', board: board?.arrivals ?? null, feed: feed.roomsArrivals },
    { field: 'departures', board: board?.departures ?? null, feed: feed.roomsDepartures },
    { field: 'breakfasts', board: board?.breakfasts ?? null, feed: feed.breakfasts },
    { field: 'halfBoards', board: board?.halfBoards ?? null, feed: feed.halfBoards },
  ];

  const known = pairs.filter((p) => p.board != null);
  if (known.length === 0) {
    return { date: feed.date, verdict: 'boardSilent', feed, deltas: [] };
  }

  const deltas = known
    .filter((p) => p.board !== p.feed)
    .map<StayFieldDelta>((p) => ({
      field: p.field,
      board: p.board,
      feed: p.feed,
      delta: p.board == null ? null : p.feed - p.board,
    }));

  return {
    date: feed.date,
    verdict: deltas.length === 0 ? 'agree' : 'differ',
    feed,
    deltas,
  };
}

export interface StayComparisonSummary {
  daysCompared: number;
  daysAgreeing: number;
  daysDiffering: number;
  daysBoardSilent: number;
  /** Agreement over the days the board actually speaks to, 0..1. Null when it
   *  speaks to none — a rate over an empty set is not 100%, it is unknown, and
   *  rendering it as 1 would be the most misleading number on the page. */
  agreementRate: number | null;
  /** Which fields disagree most, worst first. Answers the question the gate
   *  really asks: is this a scattering of desk typos, or is one figure
   *  systematically wrong? A meal row alone at the top is the signature of a
   *  wrong date convention (0125's header). */
  byField: { field: StayFieldDelta['field']; days: number; totalDelta: number }[];
}

export function summarizeStayComparison(
  comparisons: readonly StayDayComparison[],
): StayComparisonSummary {
  const silent = comparisons.filter((c) => c.verdict === 'boardSilent').length;
  const agree = comparisons.filter((c) => c.verdict === 'agree').length;
  const differ = comparisons.filter((c) => c.verdict === 'differ').length;
  const spoken = agree + differ;

  const tally = new Map<StayFieldDelta['field'], { days: number; totalDelta: number }>();
  for (const c of comparisons) {
    for (const d of c.deltas) {
      const cur = tally.get(d.field) ?? { days: 0, totalDelta: 0 };
      cur.days += 1;
      cur.totalDelta += d.delta ?? 0;
      tally.set(d.field, cur);
    }
  }

  return {
    daysCompared: comparisons.length,
    daysAgreeing: agree,
    daysDiffering: differ,
    daysBoardSilent: silent,
    agreementRate: spoken === 0 ? null : agree / spoken,
    byField: [...tally.entries()]
      .map(([field, v]) => ({ field, ...v }))
      .sort((a, b) => b.days - a.days),
  };
}

/** French labels for the compared fields — the board's own wording, so the
 *  comparison names each row exactly as the desk sees it elsewhere (R4). */
export const STAY_FIELD_LABELS_FR: Record<StayFieldDelta['field'], string> = {
  occupancy: 'Occupation',
  arrivals: 'Arrivées',
  departures: 'Départs',
  breakfasts: 'Petits-déjeuners',
  halfBoards: 'Demi-pensions',
};
